import { useMemo } from 'react';
import JSDB from 'fromKotlin/nk';
import {ORDER_SHOP_HOST} from 'utils/whatsappShareUrl';

export const BAILEYS_NEW_API_SERVER = 10;
/** Solvit Official Messaging Hub (Meta Cloud). Same jar path as Chatwoot templates. */
export const OFFICIAL_HUB_SERVER = 12;
export const OFFICIAL_HUB_DEFAULT_BASE_URL = 'https://nihulkavwhatsapp.online';

export interface WhatsAppConfig {
    instanceId: string;
    isNewApi: boolean;
    baseUrl: string;
    token: string;
    /** Backend WhatsApp server id; 4, 11, and 12 skip QR / health checks. */
    server?: number;
}

export function parseWhatsappServer(raw: unknown): number | undefined {
    if (raw === null || raw === undefined || raw === '') return undefined;
    const n = typeof raw === 'number' ? raw : Number(raw);
    return Number.isFinite(n) ? n : undefined;
}

export function isOfficialHubServer(server: number | undefined): boolean {
    return server === OFFICIAL_HUB_SERVER;
}

/** Chatwoot (4/11) and Official Hub (12) — no Baileys QR or /health. */
export function isWhatsAppServerSkipConnectionCheck(server: number | undefined): boolean {
    return server === 4 || server === 11 || server === OFFICIAL_HUB_SERVER;
}

function normalizeBaseUrl(url: string): string {
    return url.trim().replace(/\/+$/, '');
}

/** Pure mapping from a sender JSON block — used by getWhatsappConfig and tests. */
export function buildWhatsappConfigFromSender(sender: any): WhatsAppConfig | null {
    if (!sender || typeof sender !== 'object') return null;
    const instanceId = String(sender.instanceId || '').trim();
    if (!instanceId) return null;

    const server = parseWhatsappServer(sender.server);

    if (isOfficialHubServer(server)) {
        const fromSender = typeof sender.baseUrl === 'string' ? sender.baseUrl.trim() : '';
        return {
            instanceId,
            isNewApi: false,
            baseUrl: normalizeBaseUrl(fromSender || OFFICIAL_HUB_DEFAULT_BASE_URL),
            token: '',
            server,
        };
    }

    if (server === BAILEYS_NEW_API_SERVER) {
        return {
            instanceId,
            isNewApi: true,
            baseUrl: 'https://nihulkavwhatsapp.online:5003',
            token: sender.token || '',
            server,
        };
    }

    const port = server === 5 ? 5004 : 5001;
    return {
        instanceId,
        isNewApi: false,
        baseUrl: `${ORDER_SHOP_HOST}:${port}`,
        token: '',
        server,
    };
}

/**
 * Pick the active whatsapp sender block the same way the jar does
 * (`send_all_msg_function.kt`): prefer `sender2`/`sender3`/`sender4` when
 * `agent` matches the logged-in user, otherwise fall back to primary `sender`.
 *
 * Important: do NOT `find()` across [sender, sender2, …] — if both primary
 * (often Chatwoot server 11) and sender2 share the same agent name, that
 * would incorrectly treat Meta templates as active while the jar still
 * delivers via sender2 (Baileys) and the client sees raw `#pattern=…` text.
 */
export function resolveActiveWhatsappSender(parsed: any, currentAgent: string): any | null {
    if (!parsed || typeof parsed !== 'object') return null;
    const agent = String(currentAgent || '').trim();

    if (agent) {
        for (const key of ['sender2', 'sender3', 'sender4'] as const) {
            const block = parsed[key];
            if (!block || typeof block !== 'object') continue;
            if (String(block.agent || '').trim() === agent) return block;
        }
    }

    if (parsed.sender && typeof parsed.sender === 'object') return parsed.sender;
    for (const key of ['sender1', 'sender2', 'sender3', 'sender4', 'sender5'] as const) {
        if (parsed[key] && typeof parsed[key] === 'object') return parsed[key];
    }
    return null;
}

export function getWhatsappConfig(): WhatsAppConfig | null {
    try {
        const whatsappConfig = JSDB().apiConfig?.whatsapp;
        if (!whatsappConfig) return null;
        const parsed = typeof whatsappConfig === 'string' ? JSON.parse(whatsappConfig) : whatsappConfig;

        const currentAgent = String(JSDB().currentAgent || '').trim();
        const sender = resolveActiveWhatsappSender(parsed, currentAgent);
        if (!sender) return null;

        return buildWhatsappConfigFromSender(sender);
    } catch {
        return null;
    }
}

export function useWhatsAppConfig(): WhatsAppConfig | null {
    return useMemo(() => getWhatsappConfig(), []);
}

/** Rough ETA for queued sends (new WhatsApp form): ~50 docs ≈ 12 min, ~50 text ≈ 8 min. */
export function estimateQueuedBulkMinutes(
    recipientCount: number,
    kind: 'document' | 'message',
): number {
    if (recipientCount <= 0) return 1;
    const minutesPer50 = kind === 'document' ? 12 : 8;
    return Math.max(1, Math.round((recipientCount / 50) * minutesPer50));
}
